import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest, HttpResponse, HttpXsrfTokenExtractor } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { AlertService, CorrelationIdService, MakeCallService, RedirectService, TokenStorage } from '@foo/foo-web-framework';
import { AuthService } from '@foo/internet-banking-web-framework/modules/auth';

export function AuthInterceptor(req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> {
  let headerName = 'X-XSRF-TOKEN';
  let alert_count = 0;

  const tokenExtractor = inject(HttpXsrfTokenExtractor);
  const tokenStorage = inject(TokenStorage);
  const translate = inject(TranslateService);
  const redirectService = inject(RedirectService);
  const alert = inject(AlertService);
  const router = inject(Router);
  const makeCall = inject(MakeCallService);
  const correlationIdService = inject(CorrelationIdService);
  const authService = inject(AuthService);

  const appendToBody = (req: HttpRequest<any>, data: any): HttpRequest<any> => {
    if (req.body === null) {
      req = req.clone({ body: {} });
    }

    if (req.body instanceof FormData) {
      Object.keys(data).forEach(key => {
        req.body.append(key, data[key]);
      });
    } else if (typeof req.body === 'object') {
      return req.clone({ body: { ...req.body, ...data } });
    }

    return req;
  }

  const sessionExpiredRedirection = (error: any) => {
    let currentRoute = router.url;
    tokenStorage.clear();
    authService.clearDigitalOnboardingLocalStorage();

    if (!currentRoute.startsWith("/auth") && currentRoute != ((window['prefix'] ? `/${window['prefix']}` : '') + "/home") && currentRoute != ((window['prefix'] ? `/${window['prefix']}` : '') + "/")) {
      window.location.href = (window['prefix'] ? `/${window['prefix']}` : '') + "/auth/login?returnUrl=" + currentRoute;
    } else {
      window.location.href = (window['prefix'] ? `/${window['prefix']}` : '') + "/auth/login";
    }
  }

  let updateIsLoading = req.headers.has('update-loading') ? req.headers.get('update-loading') === 'true' : true;
  let updateIsLoadingDelay = req.headers.has('update-loading-delay') ? req.headers.get('update-loading-delay') === 'true' : false;
  makeCall.setLoading(true, updateIsLoading);

  // this.loaderService.visibility = true;
  const token = tokenExtractor.getToken();

  // Be careful not to overwrite an existing header of the same name.
  if (req.method !== 'GET' && req.method !== 'HEAD' && token !== null && !req.headers.has(headerName)) {
    req = req.clone({ headers: req.headers.set(headerName, token) });
  }
  if (tokenStorage.getRtoken()) {
    req = appendToBody(req, { r_token: tokenStorage.getRtoken() });
  }
  if (tokenStorage.getPublicKey()) {
    req = appendToBody(req, { public_key: tokenStorage.getPublicKey() });
  }
  if (tokenStorage.getCurrentLanguage()) {
    req = appendToBody(req, { lang: tokenStorage.getCurrentLanguage() });
  }

  req = req.clone({ headers: req.headers.set('X-Requested-With', 'XMLHttpRequest') });
  req = req.clone({ headers: req.headers.set('x-foo-correlation-id', correlationIdService.getCorrelationId()) });

  return next(req).pipe(
    tap(
      (event: any) => {
        if (event instanceof HttpResponse) {
          // this.loaderService.visibility = false;
          if (window['moduleName'] && event.body.redirect) {
            redirectService.redirect(event.body.redirect);
          } else if (event.body.exception) {
            if (event.body === 'Unauthorized' || event.body.exception === 'NotAuthorizedException'
              || event.body.exception === 'SessionExpiredException' || event.body.exception === 'InvalidTokenException') {
              if (!alert_count) {
                alert_count = 1;
                alert.emitAlert({ type: 'danger', text: translate.instant("general.sessionexpired") });
              }
              sessionExpiredRedirection(event.body);
            }
          }
        }
      }
    ),
    catchError((error: HttpErrorResponse) => {
      // this.loaderService.visibility = false
      if (error.status === 401) {
        if (!alert_count) {
          alert_count = 1;
          alert.emitAlert({ type: 'danger', text: translate.instant("general.sessionexpired") });
        }
        sessionExpiredRedirection(error);
        return EMPTY;
      }
      return throwError(() => error);
    }),
    finalize(() => {
      makeCall.setLoading(false, updateIsLoading, updateIsLoadingDelay);
    })
  );
}
